import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ShieldCheck, Leaf, Package, Sparkles } from "lucide-react";
import Reveal from "../components/Reveal";
import PageHeader from "../components/PageHeader";
import { MAKHANA_PRODUCTS } from "../data/catalogue";

const BENEFITS = [
  { icon: Sparkles, title: "Premium Export Quality", body: "Largest, whitest, crunchiest pops — sortex cleaned and size-graded to consistent buyer specification." },
  { icon: Leaf, title: "Naturally Sourced", body: "Traditional wetland cultivation across Bihar's makhana belt — sun-dried and moisture-controlled." },
  { icon: ShieldCheck, title: "Hygienically Cleaned & Graded", body: "Processed under HACCP-aligned protocols with laboratory testing for moisture and microbial parameters." },
  { icon: Package, title: "Rich in Protein & Fibre", body: "A naturally gluten-free, high-protein superfood — the fastest-growing healthy snack ingredient worldwide." },
];

export default function Makhana() {
  return (
    <>
      <PageHeader
        eyebrow="Makhana · Fox Nuts"
        title="Premium Indian Makhana, exported worldwide."
        italicWord="Premium"
        subtitle="Naturally sourced from the wetlands of Bihar — hygienically cleaned, size-graded and export-ready in raw, roasted and bulk formats for international snack, retail and private label brands."
      />

      {/* BENEFITS */}
      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe">
          <Reveal className="max-w-2xl mb-16">
            <span className="overline">Why our Makhana</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight text-brand-charcoal">A superfood built for global brands.</h2>
            <span className="divider-gold" />
            <p className="text-brand-charcoal/65 leading-relaxed text-[16px]">Rising demand for gluten-free, high-protein, plant-based snacks has made makhana one of the fastest-growing categories in international retail. We supply the raw material, the finished snack, and everything between.</p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 60} className="p-8 bg-brand-ivory-warm border border-brand-charcoal/10 hover:border-brand-gold transition-colors">
                <div className="w-11 h-11 border border-brand-green/50 flex items-center justify-center mb-5 text-brand-green">
                  <b.icon className="w-5 h-5" />
                </div>
                <h4 className="font-serif text-xl text-brand-charcoal mb-2">{b.title}</h4>
                <p className="text-brand-charcoal/60 text-[14px] leading-relaxed">{b.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-24 md:py-32 bg-brand-ivory-warm">
        <div className="container-luxe">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <span className="overline">The Makhana Range</span>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight text-brand-charcoal">Three formats. One export standard.</h2>
              <span className="divider-gold" />
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
            {MAKHANA_PRODUCTS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 80}>
                <Link to={`/makhana/${p.slug}`} className="group block" data-testid={`makhana-card-${p.slug}`}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-brand-ink">
                    <div className="absolute inset-0" style={{ backgroundColor: p.color, opacity: 0.35 }} />
                    <img src={p.image} alt={p.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/75 via-transparent to-transparent" />
                    <div className="absolute top-5 left-5 text-[10px] tracking-[0.3em] uppercase text-brand-ivory/80 border border-brand-ivory/30 px-2.5 py-1">
                      {p.grade.split("/")[0].trim()}
                    </div>
                    <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-brand-ivory">
                      <div>
                        <div className="font-serif text-2xl leading-tight">{p.name}</div>
                        <div className="text-[11px] tracking-[0.22em] uppercase text-brand-ivory/60 italic mt-1">{p.tagline}</div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between text-[12px] tracking-wide">
                    <span className="text-brand-charcoal/60">MOQ · {p.moq}</span>
                    <span className="text-brand-green font-semibold uppercase tracking-[0.2em] text-[11px]">View Details</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-ivory">
        <div className="container-luxe text-center max-w-2xl mx-auto">
          <h3 className="font-serif text-4xl text-brand-charcoal">Custom blends, private label, bulk supply.</h3>
          <p className="mt-6 text-brand-charcoal/65 leading-relaxed">From single-container orders to ongoing supply for retail chains and snack brands — our Makhana line ships in bulk cartons, retail SUPs and private label formats.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/request-quote" className="luxe-btn-primary" data-testid="makhana-cta">Request Quote <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
